"use client";

import { useState, useEffect, useRef, Suspense } from "react";
import { checkSignupAge } from "@/lib/auth/age-gate";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { signUp } from "@/lib/auth/auth";
import { useAuthStore } from "@/stores/auth-store";
import { ArrowLeft, Mail, Lock, WifiOff, Phone, User, Calendar } from "lucide-react";
import { useSearchParamValue } from "@/hooks/use-search-params-safe";
import { useTranslations } from "@/hooks/use-translations";
import { PasswordInput } from "@/components/auth/password-input";
import { validatePassword } from "@/lib/validation/password";
import { getRedirectFromParamsOrStorage, clearRedirectUrl } from "@/lib/utils/redirect-storage";
import { useNetworkStatus } from "@/hooks/use-network-status";
import { useTurnstileToken } from "@/hooks/use-turnstile-token";
import { validatePhoneNumber, getPhoneExample, formatPhoneInput } from "@/lib/validation/phone";
import { CountryCodeSelector } from "@/components/auth/country-code-selector";
import { getApiUrl } from "@/lib/api/utils";
import { PENDING_REFERRAL_STORAGE_KEY } from "@/lib/referrals/constants";
import { trackEvent, identifyUser, captureError } from "@/lib/posthog-helpers";
import { apiUrl } from "@/lib/api-base";

function SignupForm() {
  const router = useRouter();
  // Extract referral code param using safe hook to prevent React DevTools from enumerating searchParams
  const refParam = useSearchParamValue("ref");
  const redirectToParam = useSearchParamValue("redirectTo");
  const login = useAuthStore((state) => state.login);
  const { t } = useTranslations();
  const { isOnline } = useNetworkStatus();
  // Signup flow: credentials → (logged in → onboarding) OR (email confirmation needed → confirm-email).
  // Phone verification is no longer collected here — it is a just-in-time gate before the
  // first outbound action (message / connect / community post). The "phone" / "otp" steps are
  // retained only for the legacy SMS-OTP resume path and are not reached in the normal flow.
  type SignupStep = "credentials" | "phone" | "otp" | "confirm-email";
  const [currentStep, setCurrentStep] = useState<SignupStep>("credentials");
  
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+44");
  const [otpCode, setOtpCode] = useState("");
  const [userId, setUserId] = useState<string | null>(null); // Store user ID after account creation
  const [preauthToken, setPreauthToken] = useState<string | null>(null); // Store preauth token
  const [verificationSid, setVerificationSid] = useState<string | null>(null); // Store Twilio verification SID for flow isolation
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [showSignInToAccountLink, setShowSignInToAccountLink] = useState(false);
  const [referralCode, setReferralCode] = useState<string>("");
  const [manualReferralCode, setManualReferralCode] = useState<string>("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [emailValidationError, setEmailValidationError] = useState<string | null>(null);
  const [phoneValidationError, setPhoneValidationError] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  // Supabase CAPTCHA: widget provides token for signUp; we pass it to API → Supabase verifies (no app-side verification).
  const { 
    token: turnstileToken, 
    getValidToken, 
    TurnstileComponent,
    isVerifying: isTurnstileVerifying,
    isAwaitingInteraction,
    resetWidget,
  } = useTurnstileToken({ visible: true });
  
  // Track page load time
  const pageLoadTimeRef = useRef<number>(Date.now());
  const isSubmittingRef = useRef(false);
  
  // Log page load
  useEffect(() => {
    console.log("[Signup] 🚀 Page loaded", {
      timestamp: new Date().toISOString(),
      pathname: typeof window !== "undefined" ? window.location.pathname : "unknown",
    });
  }, []);

  // Re-validate phone when country code changes
  useEffect(() => {
    if (phone) {
      const validation = validatePhoneNumber(phone.trim(), countryCode);
      if (!validation.isValid) {
        setPhoneValidationError(validation.error || "Invalid phone number");
      } else {
        setPhoneValidationError(null);
      }
    } else {
      setPhoneValidationError(null);
    }
  }, [countryCode]);

  // Email validation
  const validateEmailInput = (emailValue: string) => {
    if (!emailValue) {
      setEmailValidationError(null);
      return false;
    }
    try {
      const { validateEmail } = require("@/lib/validation/email");
      const result = validateEmail(emailValue);
      if (!result.isValid) {
        setEmailValidationError(result.error || "Invalid email address");
        return false;
      }
      setEmailValidationError(null);
      return true;
    } catch {
      // Fallback validation
      const basicValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
      if (!basicValid) {
        setEmailValidationError("Invalid email format");
        return false;
      }
      setEmailValidationError(null);
      return true;
    }
  };

  // Capture referral code from URL or from pendingReferralCode (set by /r/[code] before app-store redirect)
  useEffect(() => {
    try {
      if (refParam) {
        setReferralCode(refParam);
        setManualReferralCode(refParam); // Pre-fill manual input if from URL
        return;
      }
      if (typeof window !== "undefined") {
        const pending = window.localStorage.getItem(PENDING_REFERRAL_STORAGE_KEY);
        if (pending && pending.trim()) {
          setReferralCode(pending.trim());
          setManualReferralCode(pending.trim());
        }
      }
    } catch (error) {
      // Ignore errors during SSR
    }
  }, [refParam]);



  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      setMessage(null);

      // Validate email first
      if (!validateEmailInput(email)) {
        setStatus("error");
        setMessage(emailValidationError || "Please enter a valid email address.");
        return;
      }

      // Validate password on frontend
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        setStatus("error");
        setMessage(passwordValidation.errors[0] || "Password does not meet requirements.");
        return;
      }

      // Validate password on backend
      try {
        const validationResponse = await fetch(apiUrl("/api/auth/validate-password"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password }),
        });

        const validationData = await validationResponse.json();
        if (!validationData.isValid) {
          setStatus("error");
          setMessage(validationData.errors[0] || "Password does not meet requirements.");
          return;
        }
      } catch (error) {
        console.error("Password validation error:", error);
        // Continue with signup if validation endpoint fails (non-blocking)
      }

      if (!agreeToTerms) {
        setStatus("error");
        setMessage("Please agree to the Terms of Service and Privacy Policy to create an account.");
        return;
      }

      const trimmedFirst = firstName.trim();
      const trimmedLast = lastName.trim();
      if (!trimmedFirst || !trimmedLast) {
        setStatus("error");
        setMessage("Please enter your first name and last name.");
        return;
      }

      // Same rule the server applies, so the person is told here rather than after a round trip.
      const ageCheck = checkSignupAge(dateOfBirth);
      if (!ageCheck.ok) {
        setStatus("error");
        setMessage(ageCheck.message);
        return;
      }

      const submitStartTime = Date.now();
    const timeToSubmit = submitStartTime - pageLoadTimeRef.current;
    
    console.log("[Signup] 📤 Submit started", {
      timestamp: new Date().toISOString(),
      timeToSubmit: `${timeToSubmit}ms`,
      hasToken: !!turnstileToken,
      isVerifying: isTurnstileVerifying,
    });

    setStatus("loading");

    try {
      // Get valid Turnstile token (will refresh if expired)
      const tokenStartTime = Date.now();
      const validToken = await getValidToken();
      const tokenTime = Date.now() - tokenStartTime;
      
      console.log("[Signup] 🔑 Token ready for submit", {
        hasToken: !!validToken,
        tokenTime: `${tokenTime}ms`,
        timestamp: new Date().toISOString(),
      });
      
      if (!validToken) {
        console.warn("[Signup] ⚠️ No valid token available", {
          isVerifying: isTurnstileVerifying,
          hasToken: !!turnstileToken,
        });
        // Only show error if Turnstile is not currently verifying
        if (!isTurnstileVerifying) {
          setStatus("error");
          setMessage("Verification required. Please wait for verification to complete.");
        } else {
          // Still verifying - wait a bit more
          setStatus("error");
          setMessage("Please wait for verification to complete...");
        }
        return;
      }
      
      // Attempt signup with Turnstile token
      const apiStartTime = Date.now();
      console.log("[Signup] 🌐 Calling sign-up API", {
        timestamp: new Date().toISOString(),
        tokenAttached: !!validToken,
      });
      
      let response = await signUp(email, password, validToken, {
        agreeToTerms: true,
        marketingOptIn,
        firstName: trimmedFirst,
        lastName: trimmedLast,
        dateOfBirth,
      });
      let apiTime = Date.now() - apiStartTime;
      
      console.log("[Signup] 📥 API response received", {
        success: response.success,
        apiTime: `${apiTime}ms`,
        totalTime: `${Date.now() - submitStartTime}ms`,
        timestamp: new Date().toISOString(),
      });

      if (!response.success || !response.user) {
        if (response.message?.toLowerCase().includes("verification") || response.message?.toLowerCase().includes("human verification")) {
          resetWidget();
        }
        setStatus("error");
        setMessage(response.message ?? (t("auth.couldntCreateAccount")));
        return;
      }

      // Account created. Phone verification is DEFERRED to the first outbound
      // action (message / connect / community post), so we do NOT send an SMS
      // OTP here. Two outcomes depending on the Supabase "Confirm email" setting:
      setUserId(response.user.id);
      setPreauthToken(response.preauthToken || null);

      // Case (b): Supabase requires email confirmation — no session yet. Ask the
      // user to check their inbox. (Email confirmation is the anti-bot gate here.)
      if (response.needsEmailConfirmation) {
        setStatus("idle");
        setCurrentStep("confirm-email");
        setMessage(null);
        return;
      }

      // Case (a): signUp() already established the session — finish and go to onboarding.
      await handleSuccessfulSignup(response.user.id, response.user.email);
    } catch (error) {
      console.error("[Signup] ❌ Error creating account:", error);
      captureError(error, { action: "signup_submit", email });
      setStatus("error");
      setMessage("Failed to create account. Please try again.");
    }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  // Handle phone number submission and send SMS OTP
  const handlePhoneSubmit = async () => {
    setMessage(null);
    
    // Validate phone number with country code
    const phoneValidation = validatePhoneNumber(phone.trim(), countryCode);
    if (!phoneValidation.isValid || !phoneValidation.normalized) {
      setPhoneValidationError(phoneValidation.error || "Invalid phone number");
      setStatus("error");
      return;
    }
    
    setPhoneValidationError(null);
    setStatus("loading");

    try {
      if (!preauthToken || !userId) {
        setStatus("error");
        setMessage("Missing authentication token. Please start over.");
        return;
      }

      // No Turnstile needed - valid preauth proves we passed signup's verification
      const response = await fetch(getApiUrl("/api/auth/send-sms-otp"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone: phoneValidation.normalized,
          purpose: "signup",
          preauthToken,
          userId,
          turnstileToken: undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setStatus("error");
        setMessage(data.error || "Failed to send verification code. Please try again.");
        return;
      }
      
      // CRITICAL: Store verificationSid to ensure we verify the correct OTP for this flow
      if (data.verificationSid) {
        setVerificationSid(data.verificationSid);
      }
      
      // Move to OTP step
      setOtpSent(true);
      setCurrentStep("otp");
      setStatus("idle");
      setMessage(t("auth.codeSentToPhone"));
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      console.error("[Signup] Error sending SMS OTP:", error);
      setStatus("error");
      setMessage("Failed to send verification code. Please try again.");
    }
  };

  // Handle OTP verification
  const handleOTPVerify = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setStatus("error");
      setMessage("Please enter the 6-digit code.");
      return;
    }
    
    if (!userId || !preauthToken) {
      setStatus("error");
      setMessage("Missing authentication token. Please start over.");
      return;
    }
    
    setStatus("loading");
    setMessage(null);
    
    try {
      // Validate phone number again (with country code)
      const phoneValidation = validatePhoneNumber(phone.trim(), countryCode);
      if (!phoneValidation.isValid || !phoneValidation.normalized) {
        setStatus("error");
        setMessage("Invalid phone number. Please go back and re-enter.");
        return;
      }
      
      const response = await fetch(getApiUrl("/api/auth/verify-sms-otp"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // CRITICAL: Required for Set-Cookie (fixes Android login loop)
        body: JSON.stringify({
          phone: phoneValidation.normalized,
          code: otpCode.replace(/\s+/g, ""),
          purpose: "signup",
          preauthToken,
          userId,
          verificationSid: verificationSid || undefined,
        }),
      });

      const data = await response.json();

      // Log API response for debugging
      console.log("[Signup] [SESSION_DEBUG] API response received", {
        responseOk: response.ok,
        responseStatus: response.status,
        hasData: !!data,
        dataKeys: data ? Object.keys(data) : [],
        success: data?.success,
        verified: data?.verified,
        hasSession: !!data?.session,
        sessionKeys: data?.session ? Object.keys(data?.session) : [],
        hasAccessToken: !!data?.session?.access_token,
        hasRefreshToken: !!data?.session?.refresh_token,
        error: data?.error || null,
        details: data?.details || null,
        debug: data?.debug || null,
        timestamp: new Date().toISOString(),
      });
      
      if (!response.ok || !data.success || !data.verified) {
        console.error("[Signup] [SESSION_DEBUG] SMS OTP verification failed", {
          success: data.success,
          verified: data.verified,
          message: data.error,
          details: data?.details || null,
          debug: data?.debug || null,
          timestamp: new Date().toISOString(),
        });
        setStatus("error");
        // Show the real error message if available, otherwise show generic message
        const errorMessage = data.error || data.debug || "Invalid or expired verification code. Please try again.";
        setMessage(errorMessage);
        setShowSignInToAccountLink(data?.code === "phone_already_registered");
        setOtpCode("");
        return;
      }

      // Validate session data before attempting to set it
      if (!data.session) {
        console.error("[Signup] [SESSION_DEBUG] No session in API response", {
          dataKeys: Object.keys(data),
          hasUser: !!data.user,
          responseStatus: response.status,
        });
        setStatus("error");
        setMessage(data.error || "Failed to create session. Please try again.");
        setOtpCode("");
        return;
      }

      if (!data.session.access_token || !data.session.refresh_token) {
        console.error("[Signup] [SESSION_DEBUG] Session missing required tokens", {
          hasAccessToken: !!data.session.access_token,
          hasRefreshToken: !!data.session.refresh_token,
          sessionKeys: Object.keys(data.session),
        });
        setStatus("error");
        setMessage("Session tokens are missing. Please try again.");
        setOtpCode("");
        return;
      }
      
      // OTP verified - create session and complete signup
      console.log("[Signup] [SESSION_DEBUG] Setting Supabase session", {
        hasAccessToken: !!data.session.access_token,
        hasRefreshToken: !!data.session.refresh_token,
        accessTokenLength: data.session.access_token?.length || 0,
        refreshTokenLength: data.session.refresh_token?.length || 0,
        tokenType: data.session.token_type || null,
        expiresIn: data.session.expires_in || null,
      });

      const { supabase, clearAuthBackoff, withSetSessionGuard } = await import("@/lib/supabase/client");
      clearAuthBackoff();
      try {
        const setSessionResult = await withSetSessionGuard(() =>
          supabase.auth.setSession({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          })
        );

        console.log("[Signup] [SESSION_DEBUG] setSession result", {
          hasError: !!setSessionResult.error,
          errorMessage: setSessionResult.error?.message || null,
          hasSession: !!setSessionResult.data.session,
          hasUser: !!setSessionResult.data.user,
          userId: setSessionResult.data.user?.id?.substring(0, 8) + "***" || null,
        });

        if (setSessionResult.error) {
          console.error("[Signup] [SESSION_DEBUG] setSession error", {
            error: setSessionResult.error,
            errorMessage: setSessionResult.error.message,
            errorStatus: setSessionResult.error.status,
          });
          setStatus("error");
          setMessage(`Failed to set session: ${setSessionResult.error.message}`);
          setOtpCode("");
          return;
        }

        if (!setSessionResult.data.session) {
          console.error("[Signup] [SESSION_DEBUG] setSession returned null session", {
            hasUser: !!setSessionResult.data.user,
            userId: setSessionResult.data.user?.id?.substring(0, 8) + "***" || null,
          });
          setStatus("error");
          setMessage("Failed to create session. Please try again.");
          setOtpCode("");
          return;
        }

        console.log("[Signup] [SESSION_DEBUG] Session set successfully, proceeding with signup");
      } catch (sessionError: any) {
        console.error("[Signup] [SESSION_DEBUG] Exception during setSession", {
          error: sessionError,
          errorMessage: sessionError?.message,
          errorStack: sessionError?.stack,
        });
        setStatus("error");
        setMessage(`Failed to set session: ${sessionError?.message || "Unknown error"}`);
        setOtpCode("");
        return;
      }
      
      // Complete signup
      await handleSuccessfulSignup(data.user.id, data.user.email || email);
    } catch (error) {
      console.error("[Signup] Error verifying OTP:", error);
      captureError(error, { action: "signup_verify_otp", userId: userId ?? undefined });
      setStatus("error");
      setMessage("Failed to verify code. Please try again.");
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setStatus("idle");
    setMessage(null);
    await handlePhoneSubmit();
  };

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSuccessfulSignup = async (userId: string, userEmail: string) => {

    // Detect browser language and set user preference
    const browserLanguage = typeof navigator !== "undefined" 
      ? navigator.language.split("-")[0] 
      : "en";
    
    // Associate referral code if present and set language preference
    if (userId) {
      try {
        // Set language preference
        await fetch(apiUrl("/api/user/update-language"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ language: browserLanguage }),
        }).catch(() => {
          // Ignore errors - language can be set later
        });

        // Associate referral code if present (from URL, pendingReferralCode, or manual input)
        const codeToUse = referralCode || manualReferralCode.trim();
        if (codeToUse) {
          const refResponse = await fetch(apiUrl("/api/referrals/associate"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId: userId,
              referralCode: codeToUse,
            }),
          });
          if (!refResponse.ok) {
            const errorText = await refResponse.text();
            console.warn("Failed to associate referral code:", errorText);
            // Don't fail signup, but user can try again later
          }
          // Clear pending referral from storage so it is not reused
          try {
            if (typeof window !== "undefined") {
              window.localStorage.removeItem(PENDING_REFERRAL_STORAGE_KEY);
            }
          } catch {
            // ignore
          }
        }
      } catch (error) {
        console.error("Error setting up user preferences:", error);
        // Don't fail signup if these fail
      }
    }

    // Always log user in after signup (email confirmation bypassed for development)
    // The profile will be created automatically by the database trigger
    // We use the user.id as profileId since the trigger sets profile.id = user.id
    login({
      id: userId,
      email: userEmail,
      profileId: userId, // Profile ID matches user ID (created by trigger)
      membershipTier: "free",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    identifyUser(userId, { email: userEmail });
    trackEvent("user_signed_up", { method: "email" });

    setStatus("idle");

    // Mark that user just completed signup with phone verification - prevents add-phone prompt
    // from appearing behind the tutorial modal (new users already verified phone during signup)
    try {
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem("growthsync-just-signed-up", "1");
      }
    } catch {
      // Ignore storage errors
    }
    
    // Get redirect URL from params or storage
    // Default to dashboard - the app gate will redirect to onboarding if profile is incomplete
    const redirectUrl = getRedirectFromParamsOrStorage(
      typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null,
      "/dashboard"
    );
    
    // Clear redirect storage after reading
    clearRedirectUrl();
    
    setMessage("Welcome aboard. Redirecting...");
    setTimeout(() => {
      router.push(redirectUrl);
    }, 600);
  };

  // Show offline message if trying to signup while offline
  if (!isOnline) {
    return (
      <div className="mx-auto w-full max-w-md space-y-5">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 transition-colors hover:text-primary"
        >
          <ArrowLeft className="h-4 w-4" />{t("auth.backToHome")}</Link>

        <div className="rounded-2xl border border-neutral-200/80 bg-white p-6 shadow-[0_10px_30px_-14px_rgba(15,23,42,0.12)] sm:p-8">
          <div className="flex flex-col items-center justify-center space-y-6 text-center">
            <div className="rounded-2xl bg-yellow-100 p-4">
              <WifiOff className="h-12 w-12 text-yellow-600" />
            </div>
            <div className="space-y-2">
              <h1 className="gs-h1 text-neutral-900">{t("auth.noInternetConnection")}</h1>
              <p className="max-w-md text-neutral-600">{t("auth.needInternetSignup")}</p>
              <p className="max-w-md text-sm text-neutral-500">{t("auth.pleaseConnectAndRetry")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="space-y-3 flex-1 min-h-0 overflow-y-auto pb-4 px-2 sm:px-3">
      <div className="mx-auto w-full max-w-md space-y-5">
      <Link
        href="/"
        className="inline-flex items-center gap-2 text-sm font-medium text-neutral-600 transition-colors hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" />{t("auth.backToHome")}</Link>

      {/* No card, and no shouted eyebrow: the form is the page. The body line was
          hardcoded English (and said "operators", which is not how this audience is
          described); it now uses the translated hint every locale already has. */}
      <div className="py-2 sm:py-6">
      <div className="space-y-2">
        <h1 className="gs-headline text-neutral-900">{t("auth.joinGrowthSync")}</h1>
        <p className="text-sm leading-relaxed text-neutral-600 md:text-base">{t("auth.signupProfileHint")}</p>
      </div>

      <div className="mt-6 space-y-3">

      {/* Step 1: Email and Password */}
      {currentStep === "credentials" && (
        <form className="space-y-4" onSubmit={handlePasswordSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="flex flex-col gap-2 text-sm text-neutral-700">{t("onboarding.firstName")}<div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500 pointer-events-none" />
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t("onboarding.firstName")}
                    disabled={status === "loading"}
                    autoComplete="given-name"
                    className="w-full rounded-md border border-neutral-200 bg-white pl-11 pr-4 py-3 text-base text-neutral-900 placeholder:text-neutral-400 placeholder:opacity-60 focus:outline-none focus:ring-2 focus:border-primary focus:ring-primary/20 transition-colors disabled:opacity-50"
                    style={{ colorScheme: "light" }}
                  />
                </div>
              </label>
            </div>
            <div className="space-y-2">
              <label className="flex flex-col gap-2 text-sm text-neutral-700">{t("onboarding.lastName")}<div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500 pointer-events-none" />
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder={t("onboarding.lastName")}
                    disabled={status === "loading"}
                    autoComplete="family-name"
                    className="w-full rounded-md border border-neutral-200 bg-white pl-11 pr-4 py-3 text-base text-neutral-900 placeholder:text-neutral-400 placeholder:opacity-60 focus:outline-none focus:ring-2 focus:border-primary focus:ring-primary/20 transition-colors disabled:opacity-50"
                    style={{ colorScheme: "light" }}
                  />
                </div>
              </label>
            </div>
          </div>
          <div className="space-y-2">
            <label className="flex flex-col gap-2 text-sm text-neutral-700">
              {t("auth.dateOfBirth")}
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500 pointer-events-none" />
                <input
                  type="date"
                  required
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  disabled={status === "loading"}
                  autoComplete="bday"
                  max={new Date().toISOString().slice(0, 10)}
                  className="w-full rounded-md border border-neutral-200 bg-white pl-11 pr-4 py-3 text-base text-neutral-900 focus:outline-none focus:ring-2 focus:border-primary focus:ring-primary/20 transition-colors disabled:opacity-50"
                  style={{ colorScheme: "light" }}
                />
              </div>
              <span className="text-xs text-neutral-500">{t("auth.dateOfBirthHint")}</span>
            </label>
          </div>
          <div className="space-y-2">
            <label className="flex flex-col gap-2 text-sm text-neutral-700">{t("auth.email")}<div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500 pointer-events-none" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    validateEmailInput(e.target.value);
                  }}
                  onBlur={() => validateEmailInput(email)}
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  disabled={status === "loading"}
                  className={`w-full rounded-md border bg-white pl-11 pr-4 py-3 text-base text-neutral-900 placeholder:text-neutral-400 placeholder:opacity-60 focus:outline-none focus:ring-2 transition-colors disabled:opacity-50 ${
                    emailValidationError
                      ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                      : email && !emailValidationError
                      ? "border-green-300 focus:border-green-500 focus:ring-green-200"
                      : "border-neutral-200 focus:border-primary focus:ring-primary/20"
                  }`}
                  style={{ colorScheme: "light" }}
                />
              </div>
            </label>
            {emailValidationError && (
              <p className="text-xs text-red-600">{emailValidationError}</p>
            )}
            {email && !emailValidationError && (
              <p className="text-xs text-green-700">{t("auth.emailLooksValid")}</p>
            )}
          </div>
          <PasswordInput
            value={password}
            onChange={setPassword}
            label="Password"
            placeholder={t("auth.enterPassword")}
            required
            disabled={status === "loading"}
          />
          <div className="space-y-2">
            <label className="flex flex-col gap-2 text-sm text-neutral-700">
              <span>{t("auth.referralCode")}<span className="text-neutral-500 font-normal">(optional)</span></span>
              <input
                type="text"
                value={manualReferralCode}
                onChange={(e) => setManualReferralCode(e.target.value.toUpperCase())}
                placeholder={t("auth.referralCodeHint")}
                disabled={status === "loading"}
                className="w-full rounded-md border border-neutral-200 bg-white px-4 py-3 text-base text-neutral-900 placeholder:text-neutral-400 placeholder:opacity-60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
              />
            </label>
            <p className="text-xs text-neutral-500">
              {referralCode && !manualReferralCode
                ? `From your invite link: ${referralCode}`
                : "Credits the person who referred you"}
            </p>
          </div>

          <div className="space-y-3 rounded-xl border border-neutral-200/80 bg-neutral-50/60 p-4">
            <label className="group flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={agreeToTerms}
                onChange={(e) => setAgreeToTerms(e.target.checked)}
                disabled={status === "loading"}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-primary focus:ring-primary/20"
              />
              <span className="text-sm leading-relaxed text-neutral-700">
                {t("auth.iAgreeTo")}{" "}
                <Link href="/terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary hover:underline">{t("settings.termsOfService")}</Link>
                {" "}and{" "}
                <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-primary hover:underline">{t("settings.privacyPolicy")}</Link>
                <span className="text-red-600"> *</span>
              </span>
            </label>
            <label className="group flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
                disabled={status === "loading"}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-primary focus:ring-primary/20"
              />
              <span className="text-sm leading-relaxed text-neutral-700">{t("auth.marketingOptIn")}</span>
            </label>
          </div>

          <Button
            type="submit"
            fullWidth
            loading={status === "loading" || (!turnstileToken && isTurnstileVerifying)}
            disabled={
              !firstName.trim() ||
              !lastName.trim() ||
              !dateOfBirth ||
              !agreeToTerms
            }
          >
            {!turnstileToken && isTurnstileVerifying
              ? t("auth.verifying")
              : status === "loading"
              ? t("auth.creatingAccount")
              : "Create account"}
          </Button>
        </form>
      )}

      {/* Email confirmation needed (Supabase "Confirm email" ON) — no session yet */}
      {currentStep === "confirm-email" && (
        <div className="space-y-4 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <div className="space-y-1.5">
            <h2 className="gs-h2 text-neutral-900">{t("auth.checkYourEmail")}</h2>
            <p className="text-sm leading-relaxed text-neutral-600">
              We sent a confirmation link to{" "}
              <span className="font-semibold text-neutral-900">{email}</span>. Click it to activate
              your account, then sign in to finish setting up your profile.
            </p>
          </div>
          <Link
            href="/auth/login"
            className="inline-flex w-full items-center justify-center rounded-xl gradient-primary px-4 py-3 text-sm font-bold text-white"
          >{t("auth.goToSignIn")}</Link>
          <p className="text-xs text-neutral-500">
            Didn&apos;t get it? Check spam, or{" "}
            <button
              type="button"
              onClick={() => {
                setCurrentStep("credentials");
                setMessage(null);
              }}
              className="font-semibold text-primary hover:underline"
            >
              try a different email
            </button>
            .
          </p>
        </div>
      )}

      {/* Step 2: Phone Number (for "Use a different phone number") */}
      {currentStep === "phone" && (
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="flex flex-col gap-2 text-sm text-neutral-700">{t("auth.phoneNumberLabel")}<div className="flex">
                <CountryCodeSelector
                  value={countryCode}
                  onChange={setCountryCode}
                  className="flex-shrink-0"
                />
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500 pointer-events-none" />
                  <input
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => {
                      const formattedValue = formatPhoneInput(e.target.value, countryCode);
                      setPhone(formattedValue);
                      if (formattedValue) {
                        const validation = validatePhoneNumber(formattedValue, countryCode);
                        setPhoneValidationError(validation.isValid ? null : validation.error || "Invalid phone number");
                      } else {
                        setPhoneValidationError(null);
                      }
                    }}
                    placeholder={getPhoneExample(countryCode)}
                    disabled={status === "loading"}
                    className={`w-full rounded-r-md border border-neutral-200 bg-white pl-11 pr-4 py-3 text-base text-neutral-900 placeholder:text-neutral-400 placeholder:opacity-60 focus:outline-none focus:ring-2 transition-colors disabled:opacity-50 ${
                      phoneValidationError
                        ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                        : phone && !phoneValidationError && validatePhoneNumber(phone, countryCode).isValid
                        ? "border-green-300 focus:border-green-500 focus:ring-green-200"
                        : "border-neutral-200 focus:border-primary focus:ring-primary/20"
                    }`}
                    style={{ colorScheme: "light" }}
                    autoComplete="tel"
                  />
                </div>
              </div>
            </label>
            {phoneValidationError && (
              <p className="text-xs text-red-600">{phoneValidationError}</p>
            )}
            {phone && !phoneValidationError && validatePhoneNumber(phone, countryCode).isValid && (
              <p className="text-xs text-green-700">{t("auth.phoneLooksValid")}</p>
            )}
            <p className="text-xs text-neutral-500">{t("auth.smsCodeHint")}</p>
          </div>
          
          <Button
            onClick={handlePhoneSubmit}
            fullWidth
            loading={status === "loading"}
            disabled={!validatePhoneNumber(phone, countryCode).isValid}
          >
            {status === "loading" ? t("auth.sendingCode") : t("auth.sendVerificationCode")}
          </Button>

          <button
            type="button"
            onClick={() => {
              setCurrentStep("otp");
              setPhoneValidationError(null);
            }}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />{t("auth.backToVerificationCode")}</button>
        </div>
      )}

      {/* Step 3: OTP Verification */}
      {currentStep === "otp" && (
        <form 
          className="space-y-4" 
          onSubmit={(e) => {
            e.preventDefault();
            handleOTPVerify();
          }}
        >
          <div className="space-y-2">
            <label className="flex flex-col gap-2 text-sm text-neutral-700">{t("verification.verificationCode")}<input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={otpCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtpCode(value);
                }}
                autoComplete="one-time-code"
                autoFocus
                placeholder="000000"
                disabled={status === "loading"}
                className="w-full rounded-md border border-neutral-200 bg-white px-4 py-3 text-base text-neutral-900 placeholder:text-neutral-400 placeholder:opacity-60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors text-center tracking-widest font-mono text-2xl"
                style={{ colorScheme: "light" }}
              />
            </label>
            <p className="text-sm text-neutral-600 text-center">
              Enter the 6-digit code sent to {phone ? phone.substring(0, 5) + "***" : "your phone"}
            </p>
          </div>
          
          <Button
            type="submit"
            fullWidth
            loading={status === "loading"}
            disabled={otpCode.length !== 6}
          >
            {status === "loading" ? t("auth.verifying") : t("auth.verifyCode")}
          </Button>
          
          <div className="flex flex-col items-center justify-center gap-2 text-center text-sm text-neutral-600 sm:flex-row sm:gap-3">
            <button
              type="button"
              onClick={() => {
                setCurrentStep("phone");
                setOtpCode("");
                setOtpSent(false);
              }}
              className="font-medium text-primary hover:underline"
            >{t("auth.useDifferentPhone")}</button>
            <span className="hidden text-neutral-300 sm:inline">•</span>
            <button
              type="button"
              onClick={handleResendOTP}
              className={`font-medium text-primary hover:underline ${resendCooldown > 0 ? "cursor-not-allowed opacity-50" : ""}`}
              disabled={resendCooldown > 0 || isTurnstileVerifying || status === "loading"}
            >
              {resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : t("auth.resendCode")}
            </button>
          </div>
        </form>
      )}

      {/* The security check sits directly under whichever step is on screen, because all of them
          share this single widget. It used to render down in the page footer beneath "Already have
          an account?", so when Cloudflare asked someone to tick "Verify you are human" the box was
          nowhere near the form they were filling in. */}
      {isAwaitingInteraction && (
        <p className="text-sm text-neutral-600" role="status">
          Please tick the box below to confirm you are human, then continue.
        </p>
      )}
      <TurnstileComponent />

      {message && (
        <div
          className={`rounded-xl border p-3 text-sm ${
            status === "error"
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-primary/20 bg-primary/5 text-neutral-800"
          }`}
        >
          <p>{message}</p>
          {showSignInToAccountLink && (
            <p className="mt-2">
              <Link href="/auth/login" className="font-semibold hover:underline text-primary">{t("auth.signInToThatAccount")}</Link>
            </p>
          )}
        </div>
      )}
      </div>
      </div>
      </div>
      </div>
      {/* Terms - aligned with copyright at bottom (desktop) and sticky at bottom (mobile) */}
      <div
        className="pt-8 flex-shrink-0 mt-auto border-t border-neutral-200 px-2 sm:px-3"
        style={{
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          paddingLeft: "calc(0.5rem + env(safe-area-inset-left, 0px))",
          paddingRight: "calc(0.5rem + env(safe-area-inset-right, 0px))",
        }}
      >
        <div className="mx-auto w-full max-w-md">
        <p className="mb-3 text-center text-sm leading-relaxed text-neutral-600" style={{ maxWidth: '100%', width: '100%' }}>
          {t("auth.alreadyHaveAccount")}{" "}
          <Link href="/auth/login" className="font-semibold text-primary hover:underline">{t("auth.signIn")}</Link>
        </p>
        <p className="text-xs text-neutral-500 text-center pt-4">
          {t("auth.bySigningUpAgree")}{" "}
          <Link href="/terms" className="text-primary hover:underline">{t("settings.termsOfService")}</Link>
          {", "}
          <Link href="/privacy" className="text-primary hover:underline">{t("settings.privacyPolicy")}</Link>
          {t("auth.andSeparator")}
          <Link href="/cookies" className="text-primary hover:underline">{t("settings.cookiePolicy")}</Link>.
        </p>
        </div>
      </div>
    </div>
  );
}

export default function SignupPage() {
  const { t } = useTranslations();
  return (
    <Suspense fallback={
      <div className="mx-auto w-full max-w-md py-2 sm:py-6">
        <div className="space-y-2">
          <h1 className="gs-headline text-neutral-900">{t("auth.joinGrowthSync")}</h1>
          <p className="text-sm leading-relaxed text-neutral-600 md:text-base">{t("auth.signupProfileHint")}</p>
        </div>
        <div className="py-8 text-center">
          <p className="text-neutral-500">{t("common.loading")}</p>
        </div>
      </div>
    }>
      <SignupForm />
    </Suspense>
  );
}

function InputField({
  label,
  type,
  value,
  onChange,
  required,
  placeholder,
}: {
  label: string;
  type: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  placeholder?: string;
}) {
  const getIcon = () => {
    if (type === "email") return Mail;
    if (type === "password") return Lock;
    return null;
  };
  
  const Icon = getIcon();
  
  return (
    <label className="flex flex-col gap-2 text-sm text-neutral-700">
      {label}
      <div className="relative">
        {Icon && (
          <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-500 pointer-events-none" />
        )}
        <input
          type={type}
          required={required}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className={`w-full rounded-md border border-neutral-200 bg-white ${Icon ? 'pl-11 pr-4' : 'px-4'} py-3 text-base text-neutral-900 placeholder:text-neutral-400 placeholder:opacity-60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors`}
        />
      </div>
    </label>
  );
}

